import { useQuery } from '@tanstack/react-query'
import { ApiError, hasLiveApi } from '@/api/client'
import {
  demoFancyBookmakerCommission,
  getTotalCommissionOnDetailPage,
  mapLabCommissionDetail,
  unavailableFancyBookmakerCommission,
  type FancyBookmakerCommissionQuery,
  type FancyBookmakerCommissionResult,
} from '@/api/map/fancyBookmakerCommission'
import { keys } from '@/api/hooks/shared/keys'

async function load(
  query: FancyBookmakerCommissionQuery,
): Promise<FancyBookmakerCommissionResult> {
  if (!query.memberId || !query.from || !query.to) {
    return unavailableFancyBookmakerCommission('Choose a member and a date range.')
  }
  if (!hasLiveApi()) {
    return demoFancyBookmakerCommission(query)
  }
  try {
    const res = await getTotalCommissionOnDetailPage(query)
    if (res.value === false) {
      return unavailableFancyBookmakerCommission(
        res.error?.trim() || 'Commission detail was refused by Agent-API.',
      )
    }
    return mapLabCommissionDetail(res.data)
  } catch (err) {
    if (err instanceof ApiError && (err.status === 404 || err.status === 501)) {
      // Route not deployed yet — keep Lab tiles with zeros (same as prior stub).
      return {
        unavailable: false,
        asOf: new Date().toISOString(),
        fancy: { upline: 0, downline: 0, profit: 0 },
        bookmaker: { upline: 0, downline: 0, profit: 0 },
      }
    }
    console.warn('[fancy-bookmaker-commission] hierarchy getTotalCommissionOnDetailPage failed', err)
    return unavailableFancyBookmakerCommission(
      err instanceof Error ? err.message : 'Fancy & Bookmaker commission request failed.',
    )
  }
}

export function useFancyBookmakerCommission(
  query: FancyBookmakerCommissionQuery,
  enabled = true,
) {
  return useQuery({
    queryKey: keys.fancyBookmakerCommission(query),
    queryFn: () => load(query),
    enabled: enabled && Boolean(query.memberId && query.from && query.to),
  })
}
